import { useState } from 'react';
import type { DeliverableInstance } from '@discovr/types';
import { isMain, isAddon } from '@discovr/types';

export interface DeliverableSelectionState {
  selectedMain: string | null;
  selectedAddons: Set<string>;
  toggleMain: (type: string) => void;
  toggleAddon: (type: string) => void;
  selectedTypes: string[];
  /** true when no main is selected — add-on checkboxes must be disabled. */
  isAddonDisabled: boolean;
}

/**
 * Manages main/addon deliverable selection state for CommercialPopover.
 * Seeds from the persisted is_selected flags on mount.
 *
 * IMPORTANT: The caller MUST pass key={creatorId} to CommercialPopover so this
 * hook remounts (and re-seeds) whenever the active creator changes.
 * Do NOT add a useEffect to re-sync from props here — it would fire on every
 * parent re-render (including the one triggered by onOptimisticChange inside
 * handleSave) and wipe the user's in-progress addon selection before the API
 * call even completes.
 */
export function useDeliverableSelection(
  deliverables: DeliverableInstance[],
  platform: string,
): DeliverableSelectionState {
  const persistedMain =
    deliverables.find((d) => d.is_selected && isMain(d.type, platform))?.type ?? null;
  const persistedAddonSet = new Set(
    deliverables
      .filter((d) => d.is_selected && isAddon(d.type, platform))
      .map((d) => d.type),
  );

  const [selectedMain, setSelectedMain] = useState<string | null>(persistedMain);
  const [selectedAddons, setSelectedAddons] = useState<Set<string>>(persistedAddonSet);

  const toggleMain = (type: string): void =>
    setSelectedMain((prev) => (prev === type ? null : type));

  const toggleAddon = (type: string): void =>
    setSelectedAddons((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });

  const selectedTypes = [selectedMain, ...Array.from(selectedAddons)].filter(
    (t): t is string => t !== null,
  );

  return {
    selectedMain,
    selectedAddons,
    toggleMain,
    toggleAddon,
    selectedTypes,
    isAddonDisabled: selectedMain === null,
  };
}
